import React, {
  createContext,
  useCallback,
  useImperativeHandle,
  useMemo
} from 'react'

import clsx from 'clsx'
import * as RadixTabs from '@radix-ui/react-tabs'
import { useLocation, useSearch } from 'wouter'
import { useHistoryState } from 'wouter/use-browser-location'

import { useUpdateHistoryState } from '@/Router'

export interface TabsRef {
  setTab: (newId: string) => void
}

interface Props {
  children: React.ReactNode
  headings: { id: string; text: string }[]
  areSteps?: boolean
  ref?: React.RefObject<TabsRef | null>
}

type ContextState = {
  tab: string
  setTab: (newId: string) => void
}

export const TabsContext = createContext<ContextState | null>(null)

export function Root({ children, headings, areSteps = false }: Props) {
  return (
    <RadixTabs.Root defaultValue={headings[0].id}>
      <TabsList headings={headings} areSteps={areSteps} />
      {children}
    </RadixTabs.Root>
  )
}

export function RootAsHistoryState({
  children,
  headings,
  areSteps = false,
  historyStateName,
  ref
}: Props & { historyStateName: string }) {
  const state = useHistoryState<{ [historyStateName]?: string } | null>()
  const updateState = useUpdateHistoryState()

  const id = state?.[historyStateName] ?? headings[0].id

  const setTab = useCallback(
    (newId: string) => updateState({ [historyStateName]: newId }),
    [historyStateName, updateState]
  )

  const value = useMemo(
    () => ({
      tab: id,
      setTab
    }),
    [id, setTab]
  )

  useImperativeHandle(
    ref,
    () => ({
      setTab
    }),
    [setTab]
  )

  return (
    <RadixTabs.Root
      value={id}
      onValueChange={tab => updateState({ [historyStateName]: tab })}
    >
      <TabsContext value={value}>
        <TabsList headings={headings} areSteps={areSteps} />
        {children}
      </TabsContext>
    </RadixTabs.Root>
  )
}

export function RootAsQueryParam({
  children,
  headings,
  areSteps = false,
  queryParamName,
  ref
}: Props & { queryParamName: string }) {
  const search = useSearch()
  const [location, navigate] = useLocation()

  const activeTab =
    new URLSearchParams(search).get(queryParamName) ?? headings[0].id

  const onValueChange = useCallback(
    function (tab: string) {
      const params = new URLSearchParams(search)

      params.set(queryParamName, tab)
      navigate(`${location}?${params.toString()}`, { replace: true })
    },
    [search, location, queryParamName, navigate]
  )

  const value = useMemo(
    () => ({
      tab: activeTab,
      setTab: onValueChange
    }),
    [activeTab, onValueChange]
  )

  useImperativeHandle(
    ref,
    () => ({
      setTab: onValueChange
    }),
    [onValueChange]
  )

  return (
    <RadixTabs.Root value={activeTab} onValueChange={onValueChange}>
      <TabsContext value={value}>
        <TabsList headings={headings} areSteps={areSteps} />
        {children}
      </TabsContext>
    </RadixTabs.Root>
  )
}

export function Tab({
  id,
  keepMounted,
  children
}: React.PropsWithChildren<{
  id: string
  keepMounted?: true | undefined
}>) {
  return (
    <RadixTabs.Content
      value={id}
      tabIndex={-1}
      forceMount={keepMounted}
      className={clsx(keepMounted && 'data-[state=inactive]:hidden')}
    >
      {children}
    </RadixTabs.Content>
  )
}

function TabsList({
  headings,
  areSteps
}: Pick<Props, 'headings' | 'areSteps'>) {
  return (
    <RadixTabs.TabsList className="w-full rounded-small flex flex-row inner-focus-outline">
      {headings.map(({ id, text }) => (
        <RadixTabs.Trigger
          key={id}
          value={id}
          className={clsx(
            'flex flex-row flex-1 justify-center items-center px-2 py-2 text-heading3 first:rounded-l-small last:rounded-r-small text-text data-[state=active]:border-primary data-[state=active]:text-white bg-primary/20 data-[state=active]:bg-primary outline-none',
            areSteps &&
              'not-first:not-last:-mx-[calc(1lh+4px)] first:-mr-[calc(1lh+4px)] last:-ml-[calc(1lh+4px)] [clip-path:polygon(0_0,85%_0,100%_50%,85%_100%,0_100%,15%_50%)] first:[clip-path:polygon(0_0,85%_0,100%_50%,85%_100%,0_100%)] last:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,15%_50%)] pointer-events-none'
          )}
        >
          {text}
        </RadixTabs.Trigger>
      ))}
    </RadixTabs.TabsList>
  )
}
